import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./style.css";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { RealtimeClient } from "../../Components/realtime-api-beta/index";
import BrowserOpusDecoder from "../../Components/realtime-api-beta/lib/opusDecoder";
import { WavRecorder, WavStreamPlayer48k } from "../../Components/wavtools/index";
import { MicVAD } from "@ricky0123/vad-web";
import DefaultLoader from "../LoadingPage/DefaultLoader";
import { fetchPrivateInstructions, fetchGroupInstructions } from "../../Services/coaching";
import VideoDisplay from "./components/VideoDisplay";
import SpeechControlsActivation from "./components/SpeechControlsActivation";
import StatusMessage from "./components/StatusMessage";
import { getUserFaith } from "../../Services/subscription";
import { getOpenAIVoice } from "../../Utils/CommonFunctions/voiceMapping";

function ArisCoachActivation() {
  const { setHeaderContent } = useHeader();
  const navigate = useNavigate();
  const location = useLocation();
  const user_ids = new URLSearchParams(location.search).get('user_ids');
  const relation_ids = new URLSearchParams(location.search).get('relation_ids');
  const isGroupCoaching = !!user_ids;
  const partnerIds = user_ids ? user_ids.split(',') : [];
  const relationIds = relation_ids ? relation_ids.split(',') : [];

  // Core state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConnectingText, setShowConnectingText] = useState(true);
  const [showTimeoutText, setShowTimeoutText] = useState(null);
  const [interactionState, setInteractionState] = useState("idle");
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [hasReceivedAudio, setHasReceivedAudio] = useState(false);
  const [browserAudioComplete, setBrowserAudioComplete] = useState(false);
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const [activationCompleted, setActivationCompleted] = useState(false);
  const [pendingAudioTracks, setPendingAudioTracks] = useState(false);
  const [needsAudioUnlock, setNeedsAudioUnlock] = useState(true);
  // Transient retry notice shown in place of connecting text
  const [retryNoticeText, setRetryNoticeText] = useState(null);
  const retryNoticeTimerRef = useRef(null);
  // Show a thinking/processing status between user turn end and assistant reply
  const [showThinking, setShowThinking] = useState(false);

  // Refs
  const voiceRef = useRef(null);
  const secondsRef = useRef(null);
  const userIDRef = useRef(null);
  const partnerIDsRef = useRef(partnerIds);
  const wavRecorderRef = useRef(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef(null);
  const clientRef = useRef(null);
  const connectionStartedRef = useRef(false);
  const playerConnectedRef = useRef(false);
  // Client VAD refs (mirrors ArisCoach minimal set)
  const micVadRef = useRef(null);
  const vadSpeakingRef = useRef(false);
  const attachPrerollNextFrameRef = useRef(false);
  const appendedSinceLastTurnRef = useRef(false);
  const preRollRef = useRef([]);
  const preRollSamplesRef = useRef(0);
  const MAX_PREROLL_SAMPLES = 0.3 * 16000; // 300ms @16k
  const speechEndTimerRef = useRef(null);
  const isBotSpeakingRef = useRef(false);

  // Keep isBotSpeaking ref in sync to avoid stale closures
  useEffect(() => {
    isBotSpeakingRef.current = isBotSpeaking;
  }, [isBotSpeaking]);

  // Stop local mic capture + VAD
  const stopClientAudio = useCallback(async () => {
    try {
      const rec = wavRecorderRef.current;
      if (rec && typeof rec.getStatus === "function") {
        const st = rec.getStatus();
        if (st === "recording") {
          await rec.pause();
        }
        try { await rec.end?.(); } catch { }
        try { await rec.destroy?.(); } catch { }
      }
    } catch { }
    try {
      const vad = micVadRef.current;
      if (vad) {
        try { await vad.pause?.(); } catch { }
        try { vad.destroy?.(); } catch { }
        micVadRef.current = null;
      }
    } catch { }
  }, []);

  useEffect(() => {
    // Use 48kHz for native Opus playback without resampling
    // This eliminates resampling artifacts and noise
    if (!wavStreamPlayerRef.current) {
      wavStreamPlayerRef.current = new WavStreamPlayer48k({
        sampleRate: 48000,
        bufferMs: 400, // Initial buffer (will be adjusted based on network)
        adaptiveBuffering: true // Enable network-adaptive buffering
      });
    }
    return () => {
      stopClientAudio();
      // Best-effort disconnect on unmount to avoid lingering sockets
      try { clientRef.current?.disconnect?.(); } catch (_) { }
    };
  }, [stopClientAudio]);

  // Ensure AudioContext resumes on first user interaction (autoplay policy)
  useEffect(() => {
    const resumeOnInteract = async () => {
      try {
        const p = wavStreamPlayerRef.current;
        if (p?.context && p.context.state === 'suspended') {
          await p.context.resume();
        }
        if (p?.context?.state === 'running') {
          setNeedsAudioUnlock(false);
        }
      } catch { }
    };
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach((e) => window.addEventListener(e, resumeOnInteract, { once: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, resumeOnInteract));
    };
  }, []);

  // Audio playback event listeners
  useEffect(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;
    if (!client || !wavStreamPlayer) return;

    // When new audio is added, track it
    wavStreamPlayer.on("audio.data", ({ trackId, buffer }) => {
      setHasReceivedAudio(true);
      setIsBotSpeaking(true);
      setBrowserAudioComplete(false);
      // Assistant audio has begun, stop showing thinking
      setShowThinking(false);
      setPendingAudioTracks(true);
      setInteractionState("arisSpeaking");
      setShowConnectingText(false);
    });

    // Adaptively increase playback buffer on underruns to smooth playback
    wavStreamPlayer.on("buffer.underrun", ({ trackId, graceFrames }) => {
      try {
        const current = typeof wavStreamPlayer.bufferMs === "number" ? wavStreamPlayer.bufferMs : 400;
        const next = Math.min(2000, current + 400);
        if (next !== current) {
          wavStreamPlayer.setBufferMs(next);
        }
      } catch (_e) {
      }
    });

    // When a track completes, remove it from pending
    wavStreamPlayer.on("track.complete", (trackId) => {
      setPendingAudioTracks(false);
      setIsBotSpeaking(false);
      // After assistant finishes, return to idle; VAD will update to
      // 'userSpeaking' when the user actually speaks.
      setInteractionState("idle");
    });

    // Handle activation complete
    client.realtime.on('server.activation_complete', () => {
      setActivationCompleted(true);
      // Stop sending mic audio immediately on activation completion
      try {
        const rec = wavRecorderRef.current;
        if (rec && rec.getStatus() === "recording") {
          rec.pause();
        }
      } catch (e) {
        console.warn('Error pausing recorder on activation complete:', e);
      }
    });

    client.realtime.on('conversation.item.remove', (event) => {
      setPendingAudioTracks(false);
    });

    // Listen for binary audio completion (after all chunks received)
    client.on("conversation.binary_audio_complete", ({ item_id }) => {
      console.log(`[ArisCoachActivation] Binary audio complete for item: ${item_id}`);
      wavStreamPlayer.markTrackComplete(item_id);
    });

    return () => {
      if (wavStreamPlayer) {
        wavStreamPlayer.off("track.complete");
        wavStreamPlayer.off("audio.data");
        wavStreamPlayer.off("buffer.underrun");
      }
      if (client) {
        client.realtime.off('server.activation_complete');
        client.realtime.off('conversation.item.remove');
        client.off("conversation.binary_audio_complete");
      }
    };
  }, [interactionState]);

  // Listen for all audio complete event to ensure state updates
  useEffect(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    if (!wavStreamPlayer) return;

    wavStreamPlayer.on('all.audio.complete', () => {
      setInteractionState('idle');
      setIsBotSpeaking(false);
    });

    return () => {
      wavStreamPlayer.off('all.audio.complete');
    };
  }, []);

  // Check if activation is complete and navigate
  useEffect(() => {
    if (activationCompleted && !pendingAudioTracks && browserAudioComplete) {
      navigate('/dashboard', {
        state: {
          message: 'Activation session completed.',
          reason: 'activation_complete'
        },
        replace: true
      });
    }
  }, [activationCompleted, pendingAudioTracks, browserAudioComplete, navigate]);


  // Initialize and auto-start activation session
  useEffect(() => {
    const handleInstructions = async () => {
      setHeaderContent(false);
      try {
        let response;
        if (isGroupCoaching) {
          response = await fetchGroupInstructions(relationIds[0]);
        } else {
          response = await fetchPrivateInstructions();
        }

        if (response.payload) {
          const voice = response.payload.aris_voice;
          voiceRef.current = getOpenAIVoice(voice);
          secondsRef.current = Number(response.payload.minutes) || 0;
          userIDRef.current = response.payload.data.user_id;

          try {
            const faithResponse = await getUserFaith();

            let opusDecoder = null;
            try {
              // Use 48kHz for native Opus playback without resampling
              opusDecoder = new BrowserOpusDecoder(48000, 1, false); // Enable debug logging
            } catch (e) {
              // If Opus decoder/WASM fails to initialize, log and fall back to PCM16-only.
              // Backend should continue to send codec: "pcm16" in this case.
              console.warn("Opus decoder initialization failed; falling back to PCM16-only mode", e);
              opusDecoder = null;
            }

            const clientConfig = {
              user: userIDRef.current,
              voice: voiceRef.current,
              belief_system: faithResponse && faithResponse.payload.toggle_state
                ? JSON.stringify({ belief_system: faithResponse.payload.faith })
                : '{}',
              activation: true,
              opusDecoder,
            };

            if (isGroupCoaching && partnerIDsRef.current && partnerIDsRef.current.length > 0) {
              clientConfig.user_ids = JSON.stringify(partnerIDsRef.current);
            }

            // Create client once
            if (!clientRef.current) {
              clientRef.current = new RealtimeClient(clientConfig);
            }
          } catch (error) {
            console.error("Error fetching user faith:", error);

            let opusDecoder = null;
            try {
              // Use 48kHz for native Opus playback without resampling
              opusDecoder = new BrowserOpusDecoder(48000, 1, false); // Enable debug logging
            } catch (e) {
              console.warn("Opus decoder initialization failed; falling back to PCM16-only mode", e);
              opusDecoder = null;
            }

            const clientConfig = {
              user: userIDRef.current,
              voice: voiceRef.current,
              belief_system: '{}',
              activation: true,
              opusDecoder,
            };

            if (isGroupCoaching && partnerIDsRef.current && partnerIDsRef.current.length > 0) {
              clientConfig.user_ids = JSON.stringify(partnerIDsRef.current);
            }

            if (!clientRef.current) {
              clientRef.current = new RealtimeClient(clientConfig);
            }
          } finally {
            // Auto-start the session (guard against duplicates)
            if (!connectionStartedRef.current) {
              connectionStartedRef.current = true;
              await connectConversation();
            }
          }
        } else {
          secondsRef.current = 60;
          setError(response.message);
          setLoading(false);
        }
      } catch (error) {
        setError("Server is not responding. Please try again later.");
        console.error("Error fetching instructions:", error.message);
      } finally {
        setLoading(false);
      }
    };

    // Run once on mount
    handleInstructions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const connectConversation = useCallback(async () => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    await wavRecorder.begin();
    if (!playerConnectedRef.current) {
      await wavStreamPlayer.connect();
      playerConnectedRef.current = true;
    }

    try {
      // Track activation complete for final navigation
      wavStreamPlayer.on('track.activation.complete', () => {
        setBrowserAudioComplete(true);
      });

      // Stream assistant audio to the player (mirror ArisCoach)
      const handleConversationUpdated = async ({ item, delta }) => {
        try {
          if (item?.role === 'assistant' && delta?.audio) {
            setShowConnectingText(false);
            setRetryNoticeText(null); // clear retry notice on response arrival
            setShowThinking(false);
            setHasReceivedAudio(true);
            setIsBotSpeaking(true);
            setInteractionState('arisSpeaking');
            wavStreamPlayer.add16BitPCM(delta.audio, item.id);
          }

          // Don't mark track complete here - wait for binary_audio_complete event
          // This ensures all binary chunks have arrived before marking complete
        } catch (e) {
          console.warn('Error handling conversation.updated in activation:', e);
        }
      };

      const handleClose = async () => {
        console.log("Activation session closed by server");
        try {
          if (wavRecorder && wavRecorder.getStatus() === 'recording') {
            await wavRecorder.pause();
          }
        } catch (e) {
          console.warn('Error pausing recorder on socket close:', e);
        }
      };

      // Show retry notice from server and auto-clear after 4s
      const handleRetryNotice = (evt) => {
        try {
          const content = evt?.content || "Still working on it — a quick hiccup on our end. Retrying now.";
          setRetryNoticeText(content);
          // Ensure connecting text is not shown simultaneously
          setShowConnectingText(false);
        } catch { }
      };

      const handleError = (event) => {
        console.error("Client error:", event);
        setError("Connection to Aris was interrupted. Please try again later.");
      };

      client.realtime.on('close', handleClose);
      client.on('conversation.updated', handleConversationUpdated);
      client.on('error', handleError);
      client.on('session.response_retry_notice', handleRetryNotice);

      // Ensure server VAD is disabled for client-side VAD
      try { client.updateSession({ turn_detection: null }); } catch { }

      // Avoid connecting multiple times
      const response = client.isConnected && client.isConnected() ? true : await client.connect({ activation: true });

      if (response) {
        setLoading(false);
        setIsSessionActive(true);
        setShowConnectingText(false);
        try {
          const ctx = wavStreamPlayer.context;
          if (ctx?.state === 'running') {
            setNeedsAudioUnlock(false);
          }
        } catch { }

        // Now that socket is connected, ensure VAD mode is client-side
        try { client.updateSession({ turn_detection: null }); } catch { }

        // Start recording and stream based on client VAD state
        await wavRecorder.record((data) => {
          if (!client.isConnected()) return;
          try {
            // Pre-roll buffering while not speaking
            if (!vadSpeakingRef.current) {
              // maintain ~300ms pre-roll (16k samples)
              preRollRef.current.push(data.mono);
              preRollSamplesRef.current += data.mono.length;
              while (preRollSamplesRef.current > MAX_PREROLL_SAMPLES && preRollRef.current.length > 0) {
                const removed = preRollRef.current.shift();
                preRollSamplesRef.current -= (removed?.length || 0);
              }
              return;
            }

            // Send pre-roll frames once at speech start
            if (attachPrerollNextFrameRef.current) {
              for (const frame of preRollRef.current) {
                client.appendInputAudio(frame);
              }
              preRollRef.current = [];
              preRollSamplesRef.current = 0;
              attachPrerollNextFrameRef.current = false;
            }

            // Stream live frame while speaking
            client.appendInputAudio(data.mono);
            appendedSinceLastTurnRef.current = true;
          } catch (e) {
            // swallow errors if socket closed
          }
        });

        // Initialize MicVAD and auto-start
        try {
          micVadRef.current = await MicVAD.new({
            positiveSpeechThreshold: 0.6,
            negativeSpeechThreshold: 0.25,
            redemptionMs: 1500,
            preSpeechPadMs: 300,
            minSpeechMs: 400,
            model: "v5",
            userSpeakingThreshold: 0.6,
            baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.29/dist/",
            startOnLoad: true,
            onnxWASMBasePath:
              "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.23.2/dist/",
            onSpeechStart: async () => {
              vadSpeakingRef.current = true;
              attachPrerollNextFrameRef.current = true;
              setInteractionState('userSpeaking');
              try { await wavStreamPlayer.interrupt(); } catch { }
              setIsBotSpeaking(false);
            },
            onSpeechEnd: async () => {
              vadSpeakingRef.current = false;
              try { setShowThinking(true); } catch { }
              attachPrerollNextFrameRef.current = false;
              // Debounce end slightly to avoid choppy punctuation
              if (speechEndTimerRef.current) clearTimeout(speechEndTimerRef.current);
              speechEndTimerRef.current = setTimeout(async () => {
                try {
                  if (appendedSinceLastTurnRef.current) {
                    await client.createResponse();
                  }
                } catch { }
                appendedSinceLastTurnRef.current = false;
                setInteractionState('idle');
              }, 200);
            },
            onVADMisfire: async () => {
              // False trigger: clear buffers and do not create response
              vadSpeakingRef.current = false;
              attachPrerollNextFrameRef.current = false;
              const hadAppended = !!appendedSinceLastTurnRef.current;
              appendedSinceLastTurnRef.current = false;
              preRollRef.current = [];
              preRollSamplesRef.current = 0;
              try {
                if (hadAppended) {
                  client.realtime?.send?.('input_audio_buffer.clear');
                }
              } catch { }
            },
          });
          try { await micVadRef.current.start?.(); } catch { }
        } catch (e) {
          console.warn('MicVAD init failed:', e);
        }

        // Also handle interruption to cancel server generation at current offset (safety)
        const handleInterrupted = async () => {
          try {
            const trackSampleOffset = await wavStreamPlayer.interrupt();
            const { trackId, offset } = trackSampleOffset || {};
            if (trackId) {
              await client.cancelResponse(trackId, offset);
            }
          } catch { }
          setIsBotSpeaking(false);
          setInteractionState('userSpeaking');
        };
        client.on('conversation.interrupted', handleInterrupted);

        const handleSpeakerStopped = () => {
          setInteractionState('idle');
        };
        client.on('conversation.speaker_stopped', handleSpeakerStopped);
      }

      return () => {
        client.realtime.off('close', handleClose);
        wavStreamPlayer.off('track.activation.complete');
        client.off('conversation.updated', handleConversationUpdated);
        client.off('conversation.interrupted');
        client.off('conversation.speaker_stopped');
        client.off('error');
        client.off('session.response_retry_notice');
        if (retryNoticeTimerRef.current) {
          clearTimeout(retryNoticeTimerRef.current);
          retryNoticeTimerRef.current = null;
        }
        try { micVadRef.current?.pause?.(); } catch { }
        try { micVadRef.current?.destroy?.(); } catch { }
        micVadRef.current = null;
      };
    } catch (error) {
      setError('Error connecting to Aris. Please try again later.');
      setLoading(false);
    }
  }, [navigate, hasReceivedAudio, browserAudioComplete]);


  const disconnectConversation = useCallback(async () => {
    try {
      await stopClientAudio();
    } catch { }

    setLoading(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    try {
      if (client && client.isConnected()) {
        await client.disconnect();
      }
      if (wavRecorder) {
        await wavRecorder.end();
      }
      if (wavStreamPlayer) {
        wavStreamPlayer.interrupt();
      }
      setIsSessionActive(false);
    } catch (error) {
      console.error("Error disconnecting:", error);
    } finally {
      setLoading(false);
      navigate("/dashboard");
    }
  }, [navigate, stopClientAudio]);

  const handleStopSession = async () => {
    await disconnectConversation();
  };

  return (
    <div className="coach-banner">

      {loading ? (
        <DefaultLoader />
      ) : (
        <div className="coaching-main-container">
          <VideoDisplay interactionState={interactionState} />
          <div className="coaching-main-container-text">
            <StatusMessage
              error={error}
              disconnectText={null}
              showConnectingText={showConnectingText && isSessionActive}
              showTimeoutText={showTimeoutText}
              retryNoticeText={retryNoticeText}
              thinking={showThinking}
              thinkingText={"Aris is thinking"}
              isTalking={interactionState === "arisSpeaking"}
              isListening={interactionState === "userSpeaking"}
              listeningVariant="dots"
              idleMessage={
                !isSessionActive
                  ? "Welcome! Click the mic to start your activation session"
                  : (interactionState === "idle" && !showThinking ? "Aris is ready to listen" : null)
              }
            />
          </div>
          <SpeechControlsActivation
            isSessionActive={isSessionActive}
            onStopSession={handleStopSession}
            error={error}
            secondsRef={secondsRef}
          />
        </div>
      )}
    </div>
  );
}

export default ArisCoachActivation;
